/**
 * Carreira Offline (item 5.3): uma temporada de pontos corridos contra clubes NPC, sem internet, salva no
 * navegador depois de cada rodada. Você joga a partida do seu clube; as outras da rodada são simuladas por um
 * modelo rápido pela força de cada clube (a simulação completa das outras partidas é do item 8.3).
 */
import {
  EXAMPLE_KITS,
  LEAGUE,
  nextFloat,
  roundRobin,
  seedRng,
  standings,
  type BotLevel,
  type Kit,
  type MatchStatLine,
  type Position,
  type StandingRow,
} from "@besenliga/shared";
import type { KitId, SavedAthlete } from "../creator/roster";
import { CLUB_NAMES } from "../flight/pilot";

export interface CareerClub {
  name: string;
  full: string;
  kit: Kit;
  /** Nível dos bots do clube (o seu time usa o nível dos companheiros). */
  level: BotLevel;
  you: boolean;
}

export interface MyLine {
  role: Position;
  rating: number;
  stats: MatchStatLine;
  played: boolean;
}

export interface CareerMatch {
  round: number;
  home: number;
  away: number;
  played: boolean;
  homeScore: number;
  awayScore: number;
  endReason: string | null;
  /** Sua linha, na partida do seu clube jogada por você. */
  mine: MyLine | null;
}

export type LeagueLevel = "facil" | "normal" | "dificil";

export interface Career {
  version: 1;
  seed: number;
  season: number;
  createdAt: string;
  athleteId: string | null;
  athleteName: string;
  role: Position;
  league: LeagueLevel;
  clubs: CareerClub[];
  fixtures: CareerMatch[];
  /** Campeões das temporadas anteriores (nome) e sua posição final. */
  history: { season: number; champion: string; position: number; points: number }[];
}

export const CAREER_KEY = "besenliga.carreira.v1";

/** Clubes NPC originais (cidades brasileiras e bichos daqui; nenhum clube real). */
const NPC: readonly { name: string; full: string; kit: Kit }[] = [
  {
    name: "Tucanos",
    full: "Tucanos de Sorocaba",
    kit: { primary: 0xf2c230, secondary: 0x1c1c22, shorts: 0x1c1c22, boots: 0x14141a },
  },
  {
    name: "Lontras",
    full: "Lontras de Joinville",
    kit: { primary: 0x2c5fa8, secondary: 0xffffff, shorts: 0xe8edf2, boots: 0x1d2433 },
  },
  {
    name: "Tamanduás",
    full: "Tamanduás de Uberlândia",
    kit: { primary: 0x7a4b2a, secondary: 0xf1e2c4, shorts: 0xf1e2c4, boots: 0x2a1f18 },
  },
  {
    name: "Araras",
    full: "Araras de Natal",
    kit: { primary: 0x2f9e57, secondary: 0xf6d743, shorts: 0x173c2a, boots: 0x13241a },
  },
];

export const NPC_CLUBS = NPC;

/** Força dos 5 adversários por nível da liga (do mais fraco ao mais forte). */
const LEAGUE_LEVELS: Record<LeagueLevel, readonly BotLevel[]> = {
  facil: ["facil", "facil", "facil", "medio", "medio"],
  normal: ["facil", "medio", "medio", "medio", "dificil"],
  dificil: ["medio", "medio", "dificil", "dificil", "dificil"],
};

/** Seus companheiros de time, por nível da liga. */
export const TEAMMATES: Record<LeagueLevel, BotLevel> = {
  facil: "medio",
  normal: "medio",
  dificil: "dificil",
};

export const LEAGUE_LABEL: Record<LeagueLevel, string> = {
  facil: "Liga fácil",
  normal: "Liga normal",
  dificil: "Liga difícil",
};

export interface NewCareer {
  seed: number;
  athlete: SavedAthlete | undefined;
  role: Position;
  league: LeagueLevel;
  now?: string;
}

export function createCareer(o: NewCareer): Career {
  const yourKit: KitId = o.athlete?.kit ?? "corujas-campinas";
  const otherKit: KitId = yourKit === "corujas-campinas" ? "lobos-guaiba" : "corujas-campinas";
  const levels = LEAGUE_LEVELS[o.league];
  const npcs = [
    { name: CLUB_NAMES[otherKit].short, full: CLUB_NAMES[otherKit].full, kit: EXAMPLE_KITS[otherKit] },
    ...NPC,
  ];
  const clubs: CareerClub[] = [
    {
      name: CLUB_NAMES[yourKit].short,
      full: CLUB_NAMES[yourKit].full,
      kit: EXAMPLE_KITS[yourKit],
      level: TEAMMATES[o.league],
      you: true,
    },
    // A força de cada NPC sai embaralhada pela semente, para cada carreira ter um favorito diferente.
    ...shuffle(npcs, o.seed).map((c, i) => ({ ...c, level: levels[i] ?? "medio", you: false })),
  ];
  return {
    version: 1,
    seed: o.seed,
    season: 1,
    createdAt: o.now ?? new Date().toISOString(),
    athleteId: o.athlete?.id ?? null,
    athleteName: o.athlete?.name ?? "Atleta de teste",
    role: o.role,
    league: o.league,
    clubs,
    fixtures: newFixtures(),
    history: [],
  };
}

function newFixtures(): CareerMatch[] {
  return roundRobin(LEAGUE.clubs).map((f) => ({
    ...f,
    played: false,
    homeScore: 0,
    awayScore: 0,
    endReason: null,
    mine: null,
  }));
}

function shuffle<T>(list: readonly T[], seed: number): T[] {
  const out = [...list];
  let rng = seedRng(seed);
  for (let i = out.length - 1; i > 0; i--) {
    let f: number;
    [rng, f] = nextFloat(rng);
    const j = Math.floor(f * (i + 1));
    const a = out[i];
    const b = out[j];
    if (a !== undefined && b !== undefined) {
      out[i] = b;
      out[j] = a;
    }
  }
  return out;
}

export const youIndex = (c: Career) => c.clubs.findIndex((x) => x.you);

/** Rodada atual: a primeira com partida por jogar (null = temporada encerrada). */
export function currentRound(c: Career): number | null {
  const next = c.fixtures.find((f) => !f.played);
  return next ? next.round : null;
}

/** Sua partida da rodada atual. */
export function myFixture(c: Career): CareerMatch | null {
  const r = currentRound(c);
  if (r === null) return null;
  const you = youIndex(c);
  return c.fixtures.find((f) => f.round === r && (f.home === you || f.away === you)) ?? null;
}

export const finished = (c: Career) => currentRound(c) === null;

export function table(c: Career): StandingRow[] {
  return standings(
    c.clubs.length,
    c.fixtures
      .filter((f) => f.played)
      .map((f) => ({ home: f.home, away: f.away, homeScore: f.homeScore, awayScore: f.awayScore })),
  );
}

const STRENGTH: Record<BotLevel, number> = { facil: 0, medio: 1, dificil: 2 };

/**
 * Resultado rápido de uma partida entre bots, pela força dos clubes. Calibrado nas partidas de bots do item
 * 5.1: entre iguais, uns 12 gols para cada lado; o mais forte marca mais e pega a Faísca mais vezes.
 */
export function quickResult(
  home: CareerClub,
  away: CareerClub,
  seed: number,
): { home: number; away: number } {
  let rng = seedRng(seed);
  const roll = () => {
    let f: number;
    [rng, f] = nextFloat(rng);
    return f;
  };
  const sh = STRENGTH[home.level];
  const sa = STRENGTH[away.level];
  const goals = (me: number, them: number) => Math.max(0, Math.round(7 + 5 * me - 2 * them + roll() * 8));
  let h = goals(sh, sa) * 10;
  let a = goals(sa, sh) * 10;
  if (roll() < 0.5 + 0.18 * (sh - sa)) h += 30;
  else a += 30;
  return { home: h, away: a };
}

const fixtureSeed = (c: Career, f: CareerMatch) =>
  (c.seed * 7919 + c.season * 131 + f.round * 17 + f.home * 5 + f.away) >>> 0;

/** Simula as outras partidas da rodada `round` (as que não são do seu clube). */
function simulateOthers(c: Career, round: number): void {
  const you = youIndex(c);
  for (const f of c.fixtures) {
    if (f.round !== round || f.played || f.home === you || f.away === you) continue;
    const home = c.clubs[f.home];
    const away = c.clubs[f.away];
    if (!home || !away) continue;
    const r = quickResult(home, away, fixtureSeed(c, f));
    f.homeScore = r.home;
    f.awayScore = r.away;
    f.endReason = "simulada";
    f.played = true;
  }
}

/** Placar da sua partida, do ponto de vista do seu clube. */
export interface MyResult {
  mine: number;
  theirs: number;
  endReason: string;
  line: MyLine | null;
}

/** Registra a sua partida da rodada e simula as outras. Devolve uma cópia da carreira. */
export function recordMyMatch(c: Career, r: MyResult): Career {
  const next: Career = structuredClone(c);
  const f = myFixture(next);
  if (!f) return next;
  const home = f.home === youIndex(next);
  f.homeScore = home ? r.mine : r.theirs;
  f.awayScore = home ? r.theirs : r.mine;
  f.endReason = r.endReason;
  f.mine = r.line;
  f.played = true;
  simulateOthers(next, f.round);
  return next;
}

/** Simula a sua partida sem jogar (o seu clube joga como bots), e o resto da rodada. */
export function simulateMyMatch(c: Career): Career {
  const f = myFixture(c);
  if (!f) return c;
  const you = youIndex(c);
  const home = c.clubs[f.home];
  const away = c.clubs[f.away];
  if (!home || !away) return c;
  const r = quickResult(home, away, fixtureSeed(c, f));
  const mine = f.home === you ? r.home : r.away;
  const theirs = f.home === you ? r.away : r.home;
  return recordMyMatch(c, { mine, theirs, endReason: "simulada", line: null });
}

/** Começa a próxima temporada com os mesmos clubes, guardando o campeão e a sua posição. */
export function nextSeason(c: Career): Career {
  const t = table(c);
  const you = youIndex(c);
  const champion = c.clubs[t[0]?.club ?? 0]?.full ?? "";
  const pos = t.findIndex((r) => r.club === you) + 1;
  return {
    ...structuredClone(c),
    season: c.season + 1,
    fixtures: newFixtures(),
    history: [...c.history, { season: c.season, champion, position: pos, points: t[pos - 1]?.points ?? 0 }],
  };
}

/** Sua temporada: partidas jogadas por você, média da nota e números somados. */
export function mySeason(c: Career): {
  played: number;
  rating: number | null;
  gols: number;
  passesCertos: number;
  defesas: number;
  petardos: number;
  capturas: number;
} {
  const lines = c.fixtures.flatMap((f) => (f.mine ? [f.mine] : []));
  const sum = (k: keyof MatchStatLine) => lines.reduce((n, l) => n + l.stats[k], 0);
  return {
    played: lines.length,
    rating: lines.length
      ? Math.round((lines.reduce((n, l) => n + l.rating, 0) / lines.length) * 10) / 10
      : null,
    gols: sum("gols"),
    passesCertos: sum("passesCertos"),
    defesas: sum("defesas"),
    petardos: sum("petardosAcertados"),
    capturas: sum("capturas"),
  };
}

// ---------------------------------------------------------------- salvar

type Store = Pick<Storage, "getItem" | "setItem"> & Partial<Pick<Storage, "removeItem">>;

function isCareer(v: unknown): v is Career {
  if (typeof v !== "object" || v === null) return false;
  const c = v as Partial<Career>;
  return (
    c.version === 1 &&
    typeof c.seed === "number" &&
    typeof c.season === "number" &&
    Array.isArray(c.clubs) &&
    c.clubs.length === LEAGUE.clubs &&
    c.clubs.filter((x) => x.you).length === 1 &&
    Array.isArray(c.fixtures) &&
    c.fixtures.length === (LEAGUE.clubs - 1) * LEAGUE.clubs &&
    c.fixtures.every(
      (f) =>
        typeof f.homeScore === "number" && typeof f.awayScore === "number" && typeof f.played === "boolean",
    ) &&
    Array.isArray(c.history)
  );
}

export function loadCareer(store: Pick<Storage, "getItem"> | null): Career | null {
  try {
    const raw = store?.getItem(CAREER_KEY);
    const v: unknown = raw ? JSON.parse(raw) : null;
    return isCareer(v) ? v : null;
  } catch {
    return null;
  }
}

export function saveCareer(store: Store | null, c: Career): boolean {
  try {
    store?.setItem(CAREER_KEY, JSON.stringify(c));
    return !!store;
  } catch {
    return false;
  }
}

export function deleteCareer(store: Store | null): void {
  try {
    if (store?.removeItem) store.removeItem(CAREER_KEY);
    else store?.setItem(CAREER_KEY, "null");
  } catch {
    /* nada a apagar */
  }
}
